/** @jsx jsx */

import { css, jsx } from '@emotion/core';
import { DateTime } from '@zapier/date';
import { Icon, Surface, Text, TooltipWrapper } from '@zapier/design-system';

import Link from 'app/common/components/Link';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import type { PartnerTiersType } from 'app/developer-v3/types/partnerDashboard';
import { useAvroEvent } from 'app/developer-v3/hooks';

import EmbedTierImage from './EmbedTierImage';

const styleTierAction = css`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 10px;
`;

const styleFlex = css`
  display: flex;
  gap: 10px;
  justify-content: start;
  align-content: center;
`;

// This CSS looks awful because it has to. The element and its direct child both
// need the flexbox centering styles to work correctly, and the child element is
// still using Zinnia's style encapsulation. that uses extremely strong
// selectors :(
const styleTooltipWrapperWrapper = css`
  &,
  &&&&&&& > * {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

type Props = {
  statusDeadline: string;
  statusTier: PartnerTiersType;
  isBeta: boolean;
};

export default function TierAction({
  statusDeadline,
  statusTier,
  isBeta,
}: Props) {
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  return (
    <Surface borderColor="GrayWarm4">
      <div css={styleTierAction}>
        <Text type="Body4">
          <span css={styleFlex}>
            <Text type="Body4">Partnership level</Text>
            <div css={styleTooltipWrapperWrapper}>
              <TooltipWrapper
                allowMultilineTooltip={true}
                content="The Zapier Integration Partner Program and all of its benefits are free."
                isBlock={true}
              >
                {() => (
                  <Icon
                    aria-label="Help"
                    color="GrayWarm6"
                    name="alertInfoFill"
                    size={18}
                  />
                )}
              </TooltipWrapper>
            </div>
          </span>
        </Text>
        <EmbedTierImage tier={statusTier} />
        {isBeta ? (
          <Text type="Body1">
            <Text type="Body4">After 3 months</Text> the beta tag will be
            removed. Complete the milestones above to exit beta early and earn{' '}
            <Link
              href={`${ZAPIER_APP_BASE_URL}/developer-platform/partner-program`}
              onClick={() => {
                emitPartnerUserInteractionEvent({
                  eventAction: 'click',
                  interactionGoal:
                    'Clicks on the embed tile on the beta partner program page',
                  interactionName: 'partner_program_beta_embed_cta',
                });
              }}
              target="_blank"
            >
              Partner Program benefits.
            </Link>
          </Text>
        ) : (
          <Text type="Body1">
            Next evaluation date:{' '}
            <Text type="Body3">
              <DateTime date={statusDeadline} />
            </Text>
            <br />
            <br />
            <Link
              href={`${ZAPIER_APP_BASE_URL}/developer-platform/partner-program`}
              onClick={() => {
                emitPartnerUserInteractionEvent({
                  eventAction: 'click',
                  interactionGoal:
                    'Clicks on the embed tile on the partner program page',
                  interactionName: 'partner_program_embed_cta',
                });
              }}
              target="_blank"
            >
              Explore your benefits.
            </Link>
          </Text>
        )}
      </div>
    </Surface>
  );
}
